import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, Overline, Empty, Pill } from "@/components/Widgets";
import { CalendarBlank, Plus, Trash, CheckCircle } from "@phosphor-icons/react";
import { PageGuide } from "@/components/PageGuide";

const PLATFORMS = ["Instagram", "X (Twitter)", "TikTok", "YouTube", "LinkedIn", "Facebook", "Threads"];
const STATUSES = ["scheduled", "drafting", "published"];

export default function Calendar() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", platform: "Instagram", scheduled_date: "", status: "scheduled", notes: "" });

  const load = () => api.get("/calendar").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.scheduled_date) return toast.error("Title and date required");
    try {
      await api.post("/calendar", form);
      setForm({ title: "", platform: "Instagram", scheduled_date: "", status: "scheduled", notes: "" });
      setOpen(false);
      load();
      toast.success("Added to calendar");
    } catch { toast.error("Failed to add"); }
  };

  const remove = async (id) => { await api.delete(`/calendar/${id}`); load(); toast.success("Removed"); };
  const cycle = async (item) => {
    const next = STATUSES[(STATUSES.indexOf(item.status) + 1) % STATUSES.length];
    await api.put(`/calendar/${item.id}`, { ...item, status: next });
    load();
  };

  const grouped = items.reduce((acc, it) => {
    (acc[it.scheduled_date] = acc[it.scheduled_date] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-6 fade-up">
      <PageGuide title="How the Content Calendar works" steps={[
        "Click 'Schedule post', add a title, date, platform and notes, then save.",
        "Posts are grouped by date so you can plan a steady publishing cadence.",
        "Click the status chip to cycle scheduled → drafting → published.",
        "Delete any item with the trash icon.",
      ]} />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarBlank size={22} weight="bold" className="text-primary" />
          <h3 className="font-head font-bold text-lg">Content Calendar · 24/7 Publishing Plan</h3>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="toggle-add-btn"
          className="bg-primary text-primary-foreground font-semibold px-4 py-2 text-sm font-semibold flex items-center gap-2 hover:bg-primary/90 transition-colors duration-200">
          <Plus size={16} weight="bold" /> Schedule post
        </button>
      </div>

      {open && (
        <Card testid="calendar-form">
          <form onSubmit={create} className="grid md:grid-cols-2 gap-4">
            <Input label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} placeholder="Behind-the-scenes reel" testid="cal-title" />
            <div>
              <label className="overline text-muted-foreground block mb-2">Date</label>
              <input type="date" value={form.scheduled_date} data-testid="cal-date" onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
                className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
            <Select label="Platform" value={form.platform} options={PLATFORMS} onChange={(v) => setForm({ ...form, platform: v })} testid="cal-platform" />
            <Select label="Status" value={form.status} options={STATUSES} onChange={(v) => setForm({ ...form, status: v })} testid="cal-status" />
            <div className="md:col-span-2">
              <label className="overline text-muted-foreground block mb-2">Notes</label>
              <input value={form.notes} data-testid="cal-notes" onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Caption idea, hooks…"
                className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
            </div>
            <button type="submit" data-testid="save-cal-btn" className="md:col-span-2 bg-primary text-primary-foreground font-semibold font-semibold py-2.5 text-sm hover:bg-primary/90 transition-colors duration-200">Add to calendar</button>
          </form>
        </Card>
      )}

      {items.length === 0 ? (
        <Empty>No posts scheduled yet. Plan your positive content push.</Empty>
      ) : (
        <div className="space-y-4">
          {Object.keys(grouped).sort().map((date) => (
            <div key={date}>
              <Overline className="mb-2">{date}</Overline>
              <div className="space-y-2">
                {grouped[date].map((it) => (
                  <Card key={it.id} testid={`cal-item-${it.id}`} className="flex items-center gap-4 py-3">
                    <button onClick={() => cycle(it)} data-testid={`cycle-${it.id}`} title="Change status">
                      <Pill tone={it.status === "published" ? "positive" : it.status === "drafting" ? "medium" : "neutral"}>{it.status}</Pill>
                    </button>
                    <div className="flex-1">
                      <div className="text-sm font-medium">{it.title}</div>
                      {it.notes && <div className="text-xs text-muted-foreground">{it.notes}</div>}
                    </div>
                    <Pill>{it.platform}</Pill>
                    <button onClick={() => remove(it.id)} data-testid={`del-cal-${it.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                      <Trash size={16} weight="bold" />
                    </button>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="overline text-muted-foreground block mb-2">{label}</label>
      <input value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200" />
    </div>
  );
}
function Select({ label, value, options, onChange, testid }) {
  return (
    <div>
      <label className="overline text-muted-foreground block mb-2">{label}</label>
      <select value={value} data-testid={testid} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-secondary border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-primary transition-colors duration-200 capitalize">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
